<template>
    <div id="app">
        <v-app>
            <app-snackbar />

            <router-view />

            <support-popup
                v-if="support && support.mode === 'EMAIL'"
                :email="support.email"
                :style="supportStyle"
            />
        </v-app>
    </div>
</template>

<script>
import AppSnackbar from "@components/Snackbar";
import EventBus from "@packages/event-bus";
import "vue-loading-overlay/dist/vue-loading.css";
import SupportPopup from "./components/SupportPopup.vue";
import provider from "@mixins/provider";
import colors from "@mixins/colors";
import palette from "@packages/colors";
import whitelabelColor from "@packages/util/whitelabel/whitelabelColor";
import { resolveAccountSupport } from "@packages/util/whitelabel/whitelabelSupport";

import { i18n } from "@packages/I18n";

const WIFEED_FAVICON_SVG = "/images/wifeed-favicon.svg";
const WIFEED_FAVICON_PNG = "/images/wifeed-favicon.png";

export default {
    metaInfo() {
        // Precisa assinalar a variavel reativa a uma variavel para que funcione
        const title = this.appTitle;
        return {
            titleTemplate: `${title} %s`,
            meta: [
                {
                    vmid: "app",
                    name: "description",
                    content: i18n.t("message.generics.message.metaDescription"),
                },
                {
                    name: "keywords",
                    content:
                        "WiFeed, hotspot, hotspot wifi, captive portal, sva, sva para provedores, Wi-Fi Marketing",
                },
                {
                    name: "robots",
                    content: "noindex, nofollow",
                },
            ],
        };
    },
    components: {
        AppSnackbar,
        SupportPopup,
    },

    mixins: [provider, colors],

    data() {
        return {
            spinner: null,
            isClarityInitialized: false,
        };
    },

    computed: {
        whitelabelTitle() {
            return this.$store.getters["accountWhitelabel/title"];
        },

        whitelabelFaviconUrl() {
            return this.$store.getters["accountWhitelabel/faviconUrl"];
        },

        support() {
            return resolveAccountSupport({
                provider: this.savedProvider,
                mode: this.$store.getters["accountWhitelabel/supportMode"],
                email: this.$store.getters["accountWhitelabel/supportEmail"],
            });
        },

        supportMode() {
            return this.support ? this.support.mode : null;
        },

        supportStyle() {
            const primary = this.providerColor;

            return {
                "--support-primary": primary,
                "--support-primary-hover": whitelabelColor.deriveHover(primary),
                "--support-foreground": whitelabelColor.foregroundFor(primary),
            };
        },

        appTitle() {
            if (this.whitelabelTitle) return this.whitelabelTitle;

            return (
                {
                    wcs: "WCS",
                    haggi: "Haggi",
                    tely: "Tely",
                    vivo: "Vivo",
                    ntcorp: "NTCorp",
                    "data-carrier": "Data Carrier",
                    vero: "Vero",
                    unifique: "Unifique",
                    abc: "ABC",
                    wifeed: "WiFeed",
                }[this.savedProvider] || "WiFeed"
            );
        },
    },

    watch: {
        whitelabelFaviconUrl() {
            this.setupAppHead();
        },

        supportMode: {
            immediate: true,
            handler(mode) {
                this.applySupportMode(mode);
            },
        },
    },

    async mounted() {
        this.setupAppHead();

        EventBus.$on("SHOW_SPINNER", () => {
            this.spinner = this.$loading.show({
                loader: "dots",
                color: this.getColor(palette.primary),
            });
        });

        EventBus.$on("CANCEL_SPINNER", () => this.spinner.hide());
    },

    methods: {
        setupAppHead() {
            // Os providers whitelabel tem favicon proprio; WiFeed e os demais usam o icone
            // novo, em SVG onde houver suporte e em PNG no fallback.
            const providerFavicon = {
                vivo: "/images/vivo-favicon.ico",
                vero: "/images/icon/vero.ico",
                unifique: "/images/icon/unifique.ico",
                abc: "/images/icon/abc.ico",
            }[this.savedProvider];

            const svgLink = document.getElementById("favicon-svg");
            const pngLink = document.getElementById("favicon-png");

            if (!svgLink || !pngLink) return;

            if (this.whitelabelFaviconUrl) {
                svgLink.href = pngLink.href = this.whitelabelFaviconUrl;
                svgLink.removeAttribute("type");
                pngLink.removeAttribute("type");
                return;
            }

            if (providerFavicon) {
                svgLink.href = pngLink.href = providerFavicon;
                svgLink.type = pngLink.type = "image/x-icon";
                return;
            }

            svgLink.href = WIFEED_FAVICON_SVG;
            svgLink.type = "image/svg+xml";
            pngLink.href = WIFEED_FAVICON_PNG;
            pngLink.type = "image/png";
        },
        applySupportMode(mode) {
            if (!mode) return;

            const isIntercomLoaded = typeof window.Intercom === "function";

            if (mode !== "WIFEED") {
                if (isIntercomLoaded) window.Intercom("shutdown");
                return;
            }

            if (!isIntercomLoaded) {
                this.loadIntercom();
                return;
            }

            window.Intercom("boot", {
                api_base: "https://api-iam.intercom.io",
                app_id: "xe4wwqi0",
            });
        },
        loadIntercom() {
            const isTrial = process.env.NODE_ENV === "trial";

            if (isTrial) {
                return;
            }

            const intercomSettings = document.createElement("script");

            intercomSettings.innerHTML = `
        window.intercomSettings = {
                api_base: "https://api-iam.intercom.io",
                app_id: "xe4wwqi0"
            };
      `;

            const externalScript = document.createElement("script");

            externalScript.innerHTML = `
        (function () {
            var w = window;
            var ic = w.Intercom;
            if (typeof ic === 'function') {
                ic('reattach_activator');
                ic('update', w.intercomSettings);
            } else {
                var d = document;
                var i = function () {
                i.c(arguments);
                };
                i.q = [];
                i.c = function (args) {
                i.q.push(args);
                };
                w.Intercom = i;
                var l = function () {
                var s = d.createElement('script');
                s.type = 'text/javascript';
                s.async = true;
                s.src = 'https://widget.intercom.io/widget/xe4wwqi0';
                var x = d.getElementsByTagName('script')[0];
                x.parentNode.insertBefore(s, x);
                };
                if (document.readyState === 'complete') {
                l();
                } else if (w.attachEvent) {
                w.attachEvent('onload', l);
                } else {
                w.addEventListener('load', l, false);
                }
            }
            })();
        `;

            document.body.appendChild(intercomSettings);

            document.body.appendChild(externalScript);
        },
    },
};
</script>

<style lang="scss">
@import "assets/scss/app";
.intercom-lightweight-app-launcher {
    bottom: 21px !important;
}
</style>
